import { FormikProps } from 'formik';
import { t } from 'i18next';
import { IAvailability } from '../redux/booking/bookingSlice';
import { DayOfWeekEnum, DaysOfWeek, DaysOfWeekSchedule } from '../types/booking';
import isValidUsername from './validations/userNameValidation';
import { FormValues } from './validations/officeDetailsForm';

export const days = [
  'common.days.monday',
  'common.days.tuesday',
  'common.days.wednesday',
  'common.days.thursday',
  'common.days.friday',
  'common.days.saturday',
  'common.days.sunday',
];

export const initialVisibility: DaysOfWeek | {} = days.reduce(
  (acc, day) => ({
    ...acc,
    [day]: [
      'common.days.monday',
      'common.days.tuesday',
      'common.days.wednesday',
      'common.days.thursday',
      'common.days.friday',
    ].includes(day),
  }),
  {}
);

// Initialize state with default times for each day
export const initialTimes: DaysOfWeekSchedule | {} = days.reduce(
  (acc, day) => ({
    ...acc,
    [day]: { start: '08:00', end: '17:00' },
  }),
  {}
);

function convertToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function mapToIAvailability(
  status: DaysOfWeek | {},
  schedule: DaysOfWeekSchedule | {}
): IAvailability[] {
  const dayMap: { [key: string]: DayOfWeekEnum } = {
    'common.days.monday': DayOfWeekEnum.MONDAY,
    'common.days.tuesday': DayOfWeekEnum.TUESDAY,
    'common.days.wednesday': DayOfWeekEnum.WEDNESDAY,
    'common.days.thursday': DayOfWeekEnum.THURSDAY,
    'common.days.friday': DayOfWeekEnum.FRIDAY,
    'common.days.saturday': DayOfWeekEnum.SATURDAY,
    'common.days.sunday': DayOfWeekEnum.SUNDAY,
  };

  return Object.keys(status)
    .filter((day) => status[day as keyof DaysOfWeek]) // Filter only the available days
    .map((day) => {
      const { start, end } = schedule[day as keyof DaysOfWeekSchedule];
      return {
        startTime: convertToMinutes(start),
        endTime: convertToMinutes(end),
        dayOfWeek: dayMap[day],
      };
    });
}

export function subtractMinutes(
  time: string,
  isSubstract: boolean = true,
  minutes: number = 5
): string {
  // Parse the time string into hours and minutes
  const [hours, mins] = time.split(':').map(Number);

  // Create a new Date object with the given hours and minutes
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(mins);

  // Subtract the specified minutes
  if (isSubstract) {
    date.setMinutes(date.getMinutes() - minutes);
  } else {
    date.setMinutes(date.getMinutes() + minutes);
  }

  // Format the result back to "HH:MM"
  const newHours = date.getHours().toString().padStart(2, '0');
  const newMinutes = date.getMinutes().toString().padStart(2, '0');

  return `${newHours}:${newMinutes}`;
}

export function mapFromAvailability(availability: IAvailability[]): {
  status: DaysOfWeek | {};
  schedule: DaysOfWeekSchedule | {};
} {
  const dayMap = {
    [DayOfWeekEnum.MONDAY]: 'common.days.monday',
    [DayOfWeekEnum.TUESDAY]: 'common.days.tuesday',
    [DayOfWeekEnum.WEDNESDAY]: 'common.days.wednesday',
    [DayOfWeekEnum.THURSDAY]: 'common.days.thursday',
    [DayOfWeekEnum.FRIDAY]: 'common.days.friday',
    [DayOfWeekEnum.SATURDAY]: 'common.days.saturday',
    [DayOfWeekEnum.SUNDAY]: 'common.days.sunday',
  };

  // Initialize status and schedule with defaults
  const status: DaysOfWeek | {} = days.reduce(
    (acc, day) => ({
      ...acc,
      [day]: false,
    }),
    {}
  );

  const schedule: DaysOfWeekSchedule | {} = days.reduce(
    (acc, day) => ({
      ...acc,
      [day]: { start: '08:00', end: '17:00' },
    }),
    {}
  );

  // Update status and schedule based on availability
  availability.forEach(({ dayOfWeek, startTime, endTime }) => {
    const day = dayMap[dayOfWeek];
    status[day] = true;
    schedule[day] = {
      start: `${Math.floor(startTime / 60)
        .toString()
        .padStart(2, '0')}:${(startTime % 60).toString().padStart(2, '0')}`,
      end: `${Math.floor(endTime / 60)
        .toString()
        .padStart(2, '0')}:${(endTime % 60).toString().padStart(2, '0')}`,
    };
  });

  return { status, schedule };
}

export const isRtl = (language: string) => {
  const langCode = language.split('-')[0];
  switch (langCode) {
    case 'en':
      return false;
    case 'pt':
      return false;
    case 'ar':
      return true;
    default:
      return false;
  }
};

export const isDisabledBookingModalNextButton = (
  activeStep: number,
  isProvideOnlineConsultations: boolean | null,
  isTokenExists: boolean,
  availabilitiesError: Record<string, boolean>,
  selectedVideoCallPlatformId: number | null,
  isVideoPlatformTokenExists: boolean,
  isProvideInPersonConsultations: boolean | null,
  hasServicePackages: boolean,
  url: string,
  urlExistStatus: boolean | null,
  formik: FormikProps<FormValues>
) => {
  switch (activeStep) {
    case 0:
      return !isTokenExists;
    case 1:
      return Object.values(availabilitiesError).some((error) => error);
    case 2:
      if (isProvideOnlineConsultations === null) return true;
      if (isProvideOnlineConsultations && selectedVideoCallPlatformId === null) return true;
      if (isProvideOnlineConsultations && !isVideoPlatformTokenExists) return true;
      return false;
    case 3:
      if (isProvideInPersonConsultations === null) return true;
      if (isProvideOnlineConsultations === false && isProvideInPersonConsultations === false)
        return true;
      if (isProvideInPersonConsultations && (formik.values.address === '' || !formik.isValid))
        return true;
      return false;
    case 4:
      return !hasServicePackages;
    case 5:
      if (url === '') return true;
      if (urlExistStatus === null) return true;
      if (urlExistStatus === false) return true;
      if (!isValidUsername(url, t).isValid) return true;
      return false;
    default:
      return false;
  }
};
